import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation, useSearch } from 'wouter';
import { docUrl, KHOA_URL, type BoLoc } from './loc';
import { chuanHoaUrl, dayNgan, docNgan, ghiNgan, mucDs, mucViec, rutVe, thayMuc, type MucNgan } from './ngan';
import { usePhamVi } from './du-lieu';

// Phần thuần (kiểu BoLoc, đọc URL, apBoLoc, …) ở ./loc — re-export để nơi dùng chỉ cần import từ './bo-loc'.
export * from './loc';

type GiaTriLoc = { loc: BoLoc; datLoc: (p: Partial<BoLoc>) => void; xoaLoc: () => void; trong_ngan?: boolean };
const Ctx = createContext<GiaTriLoc | null>(null);

// Ghi `moi` vào query string (null / '' = bỏ khoá đó). "Xoá lọc" (moi = null) KHÔNG bỏ brand: brand là phạm vi
// xem toàn cục (chọn ở thanh trên), không phải một bộ lọc của trang.
function ghiLoc(search: string, moi: Partial<BoLoc> | null) {
  const p = new URLSearchParams(search);
  const muc = moi ?? Object.fromEntries(Object.keys(KHOA_URL).filter((k) => k !== 'brand').map((k) => [k, null]));
  for (const [k, v] of Object.entries(muc)) {
    const ten = KHOA_URL[k as keyof BoLoc];
    if (v === null || v === '' || v === undefined) p.delete(ten);
    else p.set(ten, String(v));
  }
  return p.toString();
}
// Gõ ô tìm kiếm thì THAY lịch sử (không đẻ một mục Back cho mỗi ký tự); đổi bộ lọc thì thêm.
const chiGoTim = (moi: Partial<BoLoc> | null) => !!moi && Object.keys(moi).length === 1 && 'tim' in moi;

// Brand xem (phạm vi toàn cục) nhớ theo TỪNG người xem trên máy này. Chỉ ghi khi người dùng tự chọn ở thanh trên —
// mở link có ?brand= của người khác thì thấy đúng brand đó, nhưng không đổi lựa chọn đã nhớ của mình.
const NHO_BRAND = 'vtask:brand';
export function docBrandNho(): string | null {
  try { return localStorage.getItem(NHO_BRAND); } catch { return null; }
}
export function ghiBrandNho(b: string | null) {
  try { if (b) localStorage.setItem(NHO_BRAND, b); else localStorage.removeItem(NHO_BRAND); } catch { /* chặn lưu trữ → chỉ còn trên URL */ }
}

export function NhaBoLoc({ children }: { children: ReactNode }) {
  const search = useSearch();
  const [vi_tri, dieuHuong] = useLocation();
  // Người trong brand: server đã giới hạn về brand của họ — bỏ qua ?brand= / brand đã nhớ trên máy (có thể còn từ phiên
  // admin trên cùng máy), không thì lọc thêm theo một brand khác là ra danh sách trống.
  const pv = usePhamVi();
  const loc = useMemo(() => { const l = docUrl(search); return pv ? { ...l, brand: null } : l; }, [search, pv]);
  // URL chưa có ?brand= mà máy này đã nhớ một brand → thêm vào URL (thay, không đẻ mục Back): mọi trang, kể cả trang
  // vào từ link không kèm bộ lọc, đều ở đúng phạm vi đã chọn, và URL luôn gửi đi được.
  useEffect(() => {
    if (pv || loc.brand) return;
    const nho = docBrandNho();
    if (nho) {
      const p = new URLSearchParams(search);
      p.set(KHOA_URL.brand, nho);
      dieuHuong(vi_tri + '?' + p.toString(), { replace: true });
    }
  }, [pv, loc.brand, search, vi_tri, dieuHuong]);
  const ghi = useCallback(
    (moi: Partial<BoLoc> | null) => {
      const q = ghiLoc(search, moi);
      dieuHuong(vi_tri + (q ? '?' + q : ''), { replace: chiGoTim(moi) });
    },
    [search, vi_tri, dieuHuong],
  );
  const gia_tri = useMemo(() => ({ loc, datLoc: (p: Partial<BoLoc>) => ghi(p), xoaLoc: () => ghi(null) }), [loc, ghi]);
  return <Ctx.Provider value={gia_tri}>{children}</Ctx.Provider>;
}

export function useBoLoc() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useBoLoc ngoài NhaBoLoc');
  return c;
}

// Chọn brand xem ở thanh trên: đổi URL + nhớ trên máy này ("Tất cả" = quên).
export function useBrandXem(): [string | null, (b: string | null) => void] {
  const { loc, datLoc } = useBoLoc();
  return [loc.brand, (b) => { ghiBrandNho(b); datLoc({ brand: b }); }];
}

/* ── Chồng ngăn (logic thuần ở ./ngan.ts) ── */

// Mỗi lần mở ngăn để lại dấu trong history.state: URL NGAY TRƯỚC khi mở. Đóng ngăn trên cùng mà mục lịch sử liền
// trước đúng là trạng thái sau khi đóng → history.back() thay vì đẻ mục mới. Nhờ vậy: Esc đóng một ngăn, rồi Back
// của trình duyệt đóng tiếp ngăn dưới (không mở lại ngăn vừa đóng). Mở từ link gửi sang (không có dấu) → đẩy mục
// mới như thường.
const DAU = 'vtask_ngan_tu';

export function useNgan() {
  const search = useSearch();
  const [vi_tri, dieuHuong] = useLocation();
  const ds = useMemo(() => docNgan(search), [search]);
  const den = useCallback(
    (moi: MucNgan[], replace = false) => {
      const q = ghiNgan(search, moi);
      dieuHuong(vi_tri + (q ? '?' + q : ''), { replace, state: replace ? history.state : { [DAU]: chuanHoaUrl(vi_tri, search) } });
    },
    [search, vi_tri, dieuHuong],
  );
  const day = useCallback((m: MucNgan) => {
    const moi = dayNgan(ds, m);
    if (moi !== ds) den(moi);
  }, [ds, den]);
  const ve = useCallback((i: number) => {
    const moi = rutVe(ds, i);
    if (moi.length === ds.length) return;
    const dich = chuanHoaUrl(vi_tri, ghiNgan(search, moi));
    if (moi.length === ds.length - 1 && (history.state as Record<string, unknown> | null)?.[DAU] === dich) history.back();
    else den(moi);
  }, [ds, den, search, vi_tri]);
  const dong = useCallback(() => ve(ds.length - 2), [ve, ds.length]);
  const thay = useCallback((i: number, m: MucNgan, replace = false) => den(thayMuc(ds, i, m), replace), [ds, den]);
  return useMemo(() => ({ ds, day, dong, ve, thay }), [ds, day, dong, ve, thay]);
}

// Việc đang mở = ngăn VIỆC trên cùng của chồng (link cũ ?viec=<so> vẫn đọc được). Gọi dat(so) = mở việc đó thành
// một ngăn chồng lên trên; dat(null) = đóng ngăn trên cùng.
export function useViecDangMo(): [number | null, (so: number | null) => void] {
  const { ds, day, dong } = useNgan();
  const tren = ds[ds.length - 1];
  const so = tren?.loai === 'viec' ? tren.so : null;
  const dat = useCallback((moi: number | null) => (moi ? day(mucViec(moi)) : dong()), [day, dong]);
  return [so, dat];
}

// Mở một danh sách đã lọc thành ngăn (ô Tổng quan, bảng Theo thương hiệu, tiêu đề cột Theo brand).
export function useMoDanhSach() {
  const { day } = useNgan();
  return useCallback((duong: '/danh-sach' | '/can-duyet', q = '') => day(mucDs(duong, q)), [day]);
}

// Bộ lọc của MỘT ngăn danh sách: đọc từ query của chính ngăn đó (không phải của trang bên dưới), đổi bộ lọc thì
// ghi lại vào mục đó trên chồng. Ngăn không tự chọn brand thì theo brand xem toàn cục.
export function NhaBoLocNgan({ i, muc, children }: { i: number; muc: Extract<MucNgan, { loai: 'ds' }>; children: ReactNode }) {
  const ngoai = useBoLoc();
  const { thay } = useNgan();
  const gia_tri = useMemo<GiaTriLoc>(() => {
    const rieng = docUrl(muc.q);
    const loc = { ...rieng, brand: rieng.brand ?? ngoai.loc.brand };
    const ghi = (moi: Partial<BoLoc> | null) => thay(i, mucDs(muc.duong, ghiLoc(muc.q, moi)), chiGoTim(moi));
    return { loc, datLoc: (p) => ghi(p), xoaLoc: () => ghi(null), trong_ngan: true };
  }, [muc, i, thay, ngoai.loc.brand]);
  return <Ctx.Provider value={gia_tri}>{children}</Ctx.Provider>;
}

// Link sang trang khác GIỮ bộ lọc hiện tại (nhưng bỏ ?viec / ?ngan — ngăn thuộc về trang đang mở — và ?xem, kiểu
// xem riêng của từng trang). chi_brand: trang không có thanh lọc (Tổng quan, KPI…) chỉ mang theo brand xem.
export function useGiuLoc() {
  const search = useSearch();
  return (duong: string, chi_brand = false) => {
    const p = new URLSearchParams(search);
    if (chi_brand) {
      const b = p.get(KHOA_URL.brand);
      return duong + (b ? '?' + new URLSearchParams({ [KHOA_URL.brand]: b }).toString() : '');
    }
    p.delete('viec');
    p.delete('ngan');
    p.delete('xem');
    const q = p.toString();
    return duong + (q ? '?' + q : '');
  };
}
